import React, { useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import mermaid from 'mermaid';
import 'katex/dist/katex.min.css';

// Mermaid component for rendering diagrams in activities
const ActivityMermaidDiagram = ({ chart }) => {
  const ref = useRef(null);
  const diagramId = useRef(`activity-mermaid-${Date.now()}-${Math.floor(Math.random() * 1000)}`);

  useEffect(() => {
    if (ref.current && chart) {
      // Initialize Mermaid for activities
      mermaid.initialize({
        startOnLoad: false,
        theme: 'default',
        securityLevel: 'loose',
        flowchart: {
          useMaxWidth: true,
          htmlLabels: true
        }
      });
      
      const renderChart = async () => {
        try {
          // Clear previous content
          ref.current.innerHTML = '';
          
          // Validate chart content
          if (!chart.trim()) {
            throw new Error('Empty chart content');
          }
          
          // Use mermaid.render with proper ID
          const { svg } = await mermaid.render(diagramId.current, chart.trim());
          ref.current.innerHTML = svg;
        } catch (error) {
          ref.current.innerHTML = `
            <div class="activity-mermaid-error">
              <strong>Error rendering diagram:</strong> ${error.message}
              <br><small>Please check your Mermaid syntax</small>
            </div>
          `;
        }
      };

      renderChart();
    }
  }, [chart]);

  return <div ref={ref} className="activity-mermaid-container" />;
};

// Custom renderer for code blocks to handle Mermaid in activities
const ActivityCodeBlock = ({ node, inline, className, children, ...props }) => {
  const match = /language-(\w+)/.exec(className || '');
  const language = match ? match[1] : '';

  if (!inline && language === 'mermaid') {
    return <ActivityMermaidDiagram chart={String(children).replace(/\n$/, '')} />;
  }

  return (
    <code className={className} {...props}>
      {children}
    </code>
  );
};

// Specialized ContentRenderer for Activities
const ActivityContentRenderer = ({ content }) => {
  if (!content) {
    return (
      <div className="activity-content-placeholder">
        <p>No activity content to display.</p>
        <div className="activity-content-examples">
          <h3>Supported Formats:</h3>
          <ul>
            <li><strong>Markdown:</strong> Standard markdown formatting</li>
            <li><strong>LaTeX Math:</strong> Inline math with $...$ or block math with $$...$$</li>
            <li><strong>Mermaid Diagrams:</strong> Use ```mermaid code blocks</li>
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div className="activity-content-renderer">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeKatex, rehypeRaw]}
        components={{
          code: ActivityCodeBlock,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default ActivityContentRenderer; 